import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import FeedbackPopup from '../components/FeedbackPopup';
import { SEEDED_WAITLIST_COUNT, BACKEND_URL } from '../constants';
import { 
  Shield, 
  CreditCard, 
  Bell, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  DollarSign,
  Zap,
  Lock,
  Eye,
  Calendar,
  Activity,
  Target,
  TrendingUp
} from 'lucide-react';
import '../styles/LandingPage.css';
import '../styles/TrialsPage.css';

const TrialsPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [priorityProblem, setPriorityProblem] = useState('');
  const [otherProblem, setOtherProblem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(SEEDED_WAITLIST_COUNT);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waitlist/count`);
        const data = await response.json();
        setWaitlistCount(SEEDED_WAITLIST_COUNT + data.count);
      } catch (error) {
        // Keep default value
      }
    };
    
    fetchWaitlistCount();
  }, []);

  // Handle demo click
  const handleDemoClick = useCallback((url) => {
    localStorage.setItem('demoOpened', 'true');
    
    if (window.posthog) {
      window.posthog.capture('demo_clicked_trials', {
        source_page: '/trials'
      });
    }
    
    window.open(url, '_blank');
  }, []);

  // Check for demo feedback
  useEffect(() => {
    let timeoutId;
    let intervalId;
    
    const showPopupIfNeeded = (source = 'unknown') => {
      const demoOpened = localStorage.getItem('demoOpened');
      
      if (demoOpened === 'true' && !showFeedbackPopup) {
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          setShowFeedbackPopup(true);
          
          if (window.posthog) {
            window.posthog.capture('demo_feedback_popup_shown', {
              trigger_source: source,
              source_page: '/trials'
            });
          }
          
          localStorage.removeItem('demoOpened');
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        showPopupIfNeeded('visibilitychange');
      }
    };

    const handleFocus = () => {
      showPopupIfNeeded('focus');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    showPopupIfNeeded('mount');
    
    intervalId = setInterval(() => {
      if (!document.hidden) {
        const demoOpened = localStorage.getItem('demoOpened');
        if (demoOpened === 'true' && !showFeedbackPopup) {
          showPopupIfNeeded('interval');
        }
      }
    }, 2000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [showFeedbackPopup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit feedback');
      }
      
      if (window.posthog) {
        window.posthog.capture('feedback_submitted_success', {
          source_page: '/trials'
        });
      }
    } catch (error) {
      if (window.posthog) {
        window.posthog.capture('feedback_submission_failed', {
          error: error.message,
          source_page: '/trials'
        });
      }
    }
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    const submissionData = {
      email,
      country: country || null,
      priority_problem: priorityProblem === 'other' ? otherProblem : priorityProblem,
      source_page: '/trials',
      source_campaign: 'trial_to_paid',
      signup_url: window.location.href,
      referrer_url: document.referrer || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      setIsSubmitting(false);
      setSubmitSuccess(true);
      setWaitlistCount(prev => prev + 1);
      setEmail('');
      setCountry('');
      setPriorityProblem('');
      setOtherProblem('');
      
      if (window.posthog) {
        window.posthog.capture('waitlist_submitted_trials', {
          source_page: '/trials',
          source_campaign: 'trial_to_paid',
          priority_problem: submissionData.priority_problem,
          has_utm: !!(urlParams.get('utm_source') || urlParams.get('utm_campaign'))
        });
      }
      
      // Redirect to survey
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/trials',
          source_campaign: 'trial_monitoring',
          priority_problem: submissionData.priority_problem
        }
      });
      
    } catch (error) {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      pendingSubmissions.push(submissionData);
      localStorage.setItem('pendingWaitlist', JSON.stringify(pendingSubmissions));
      
      setIsSubmitting(false);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_joined_offline', {
          saved_to_backup: true,
          source_page: '/trials',
          priority_problem: submissionData.priority_problem,
        });
      }
      
      // Redirect to survey even for offline signups
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/trials',
          source_campaign: 'trial_monitoring',
          priority_problem: submissionData.priority_problem
        }
      });
    }
  };

  // Track page view
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('page_view_trials', {
        page_path: '/trials',
        source_campaign: 'trial_to_paid'
      });
    }
  }, []);

  return (
    <div className="trials-page">
      {/* Hero Section */}
      <section className="trials-hero-section">
        <div className="trials-hero-container">
          <div className="trials-hero-content">
            <h1 className="trials-hero-title">
              Stop Forgetting to Cancel Free Trials
            </h1>
            <p className="trials-hero-subtitle">
              Get alerted before free trials turn into paid subscriptions, so you can cancel before the charge happens.
            </p>
            <p className="trials-hero-support">
              No more surprise charges. No more forgotten renewals. No more checking your calendar manually.
            </p>
            
            <div className="trials-hero-ctas">
              <Button 
                onClick={() => document.getElementById('trials-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
                className="trials-cta-primary"
              >
                Join Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
                variant="outline"
                className="trials-cta-secondary"
              >
                Explore Interactive Demo
              </Button>
            </div>
          </div>
          
          {/* Alert Card Demo */}
          <div className="trials-alert-demo">
            <Card className="trials-alert-card">
              <CardContent className="trials-alert-content">
                <div className="alert-header">
                  <Bell className="alert-bell-icon-animated" />
                  <span className="alert-badge-warning">Free Trial Ending Soon</span>
                </div>
                
                <div className="alert-body-large">
                  <h3 className="alert-service-name">Adobe Creative Cloud</h3>
                  <div className="alert-details-grid">
                    <div className="alert-detail-row">
                      <Calendar className="alert-icon" />
                      <span className="alert-text">Renews in 2 days</span>
                    </div>
                    <div className="alert-detail-row">
                      <DollarSign className="alert-icon" />
                      <span className="alert-text-price">Next charge: $29.99</span>
                    </div>
                  </div>
                </div>
                
                <Button className="alert-action-btn">
                  Cancel Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="trials-problem-section">
        <div className="section-container">
          <h2 className="section-title">Free Trials Are Easy to Forget</h2>
          <p className="section-description-large">
            Most free trials are designed to feel harmless at signup. But once the trial ends, 
            the first paid charge can happen silently.
          </p>
          
          <div className="problem-grid">
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">The trial started days or weeks ago</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">The renewal date is hidden in an email</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">The charge appears under a different merchant name</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Cancellation pages are hard to find</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Reminders are manual and easy to miss</p>
            </div>
          </div>
          
          <p className="trials-closing-line">
            <AlertCircle className="inline-icon-warning" />
            A small forgotten trial can quickly become months of unnecessary payments.
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="trials-solution-section">
        <div className="section-container">
          <div className="solution-header">
            <Activity className="solution-icon-large" />
            <h2 className="section-title">A Proactive Trial-to-Paid Watchdog</h2>
          </div>
          
          <p className="section-description-large">
            MySentinel watches for trial-based subscriptions and upcoming renewal risks, 
            then alerts you before the first paid charge happens.
          </p>
          
          <p className="section-description">
            Instead of discovering the charge after it hits your card, you get a timely reminder 
            with the next best action.
          </p>
          
          <div className="solution-actions-grid">
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <Target className="solution-action-icon" />
                <h3 className="solution-action-title">Cancel before renewal</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <CheckCircle2 className="solution-action-icon" />
                <h3 className="solution-action-title">Review whether you still need the service</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <Zap className="solution-action-icon" />
                <h3 className="solution-action-title">Open the fastest cancellation path</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-action-card">
              <CardContent className="solution-action-content">
                <Bell className="solution-action-icon" />
                <h3 className="solution-action-title">Prepare a cancellation email if needed</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monitor Everything Section */}
      <section className="trials-monitor-everything-section">
        <div className="section-container">
          <h2 className="section-title">Monitor Everything. Not Just One Account.</h2>
          <p className="section-description-large">
            Financial leaks rarely exist in a single place.
          </p>
          
          <p className="monitor-intro-text">
            Most people have multiple:
          </p>
          
          <div className="multiple-items-grid">
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Bank accounts</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Credit cards</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Subscriptions</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Recurring bills</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Service providers</span>
            </div>
          </div>
          
          <p className="monitor-pattern-text">
            Reviewing them individually makes it easy to miss important patterns.
          </p>
          
          <p className="section-description-large">
            MySentinel is designed to securely monitor your connected financial accounts together and identify 
            opportunities, risks, and money leaks across your entire financial ecosystem.
          </p>
          
          <div className="monitor-cards-grid">
            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Activity className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bank Accounts</h3>
                <p className="monitor-card-description">
                  Monitor multiple connected accounts in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <CreditCard className="monitor-card-icon" />
                <h3 className="monitor-card-title">Credit Cards</h3>
                <p className="monitor-card-description">
                  Track utilization, balances, and interest risks across cards.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Bell className="monitor-card-icon" />
                <h3 className="monitor-card-title">Subscriptions</h3>
                <p className="monitor-card-description">
                  Detect recurring charges and forgotten services.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <TrendingUp className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bills</h3>
                <p className="monitor-card-description">
                  Monitor telecom, insurance, utilities, and other recurring expenses.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="monitor-highlight-box">
            <Shield className="monitor-highlight-icon" />
            <p className="monitor-highlight-text">Connect once. Monitor everything.</p>
          </div>
          
          <div className="monitor-supporting-content">
            <p className="section-description-large">
              The most valuable financial insights often appear when information is viewed across multiple 
              accounts rather than in isolation.
            </p>
            
            <h3 className="monitor-examples-title">Examples:</h3>
            <div className="monitor-examples">
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>One card approaching high utilization while another remains underused</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Duplicate subscriptions across different payment methods</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Rising recurring costs spread across multiple providers</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Hidden money leaks that are difficult to see when reviewing accounts individually</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="trials-how-section">
        <div className="section-container">
          <h2 className="section-title">How Trial Protection Works</h2>
          
          <div className="how-steps-grid">
            <Card className="how-step-card">
              <CardContent className="how-step-content">
                <div className="step-number">1</div>
                <h3 className="step-title">Detect trial or subscription activity</h3>
                <p className="step-description">
                  MySentinel monitors your connected accounts for trial sign-ups and subscription patterns.
                </p>
              </CardContent>
            </Card>
            
            <Card className="how-step-card">
              <CardContent className="how-step-content">
                <div className="step-number">2</div>
                <h3 className="step-title">Predict upcoming renewal or paid conversion</h3>
                <p className="step-description">
                  The system calculates when free trials will convert to paid subscriptions.
                </p>
              </CardContent>
            </Card>
            
            <Card className="how-step-card">
              <CardContent className="how-step-content">
                <div className="step-number">3</div>
                <h3 className="step-title">Send alerts before the charge happens</h3>
                <p className="step-description">
                  You receive timely notifications with recommended actions before any charges occur.
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Notification Timing */}
          <div className="notification-timing-box">
            <h3 className="timing-title">Notification Timing</h3>
            <div className="timing-badges">
              <div className="timing-badge">
                <Calendar className="timing-icon" />
                <span>3 days before renewal</span>
              </div>
              <div className="timing-badge">
                <Calendar className="timing-icon" />
                <span>1 day before renewal</span>
              </div>
              <div className="timing-badge">
                <Bell className="timing-icon" />
                <span>Same-day reminder if action pending</span>
              </div>
            </div>
          </div>
          
          {/* Alert Channels */}
          <div className="alert-channels-box">
            <h3 className="channels-title">Alert Channels</h3>
            <div className="channels-grid">
              <div className="channel-item">
                <Bell className="channel-icon" />
                <span>Email alerts</span>
              </div>
              <div className="channel-item">
                <Bell className="channel-icon" />
                <span>Push notifications</span>
              </div>
              <div className="channel-item">
                <Bell className="channel-icon" />
                <span>In-app reminders</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Assisted Cancellation Section */}
      <section className="trials-cancellation-section">
        <div className="section-container">
          <h2 className="section-title">Cancel Faster With AI-Assisted Guidance</h2>
          <p className="section-description-large">
            When a trial or subscription needs action, MySentinel helps reduce the work.
          </p>
          
          <div className="cancellation-features-grid">
            <Card className="cancellation-feature-card">
              <CardContent className="cancellation-feature-content">
                <Zap className="cancellation-icon" />
                <h3 className="cancellation-feature-title">Find the fastest cancellation route</h3>
                <p className="cancellation-feature-desc">
                  Identify the quickest path to cancel without searching through settings.
                </p>
              </CardContent>
            </Card>
            
            <Card className="cancellation-feature-card">
              <CardContent className="cancellation-feature-content">
                <Target className="cancellation-icon" />
                <h3 className="cancellation-feature-title">Provide direct cancellation links</h3>
                <p className="cancellation-feature-desc">
                  Get direct links to cancellation pages where available.
                </p>
              </CardContent>
            </Card>
            
            <Card className="cancellation-feature-card">
              <CardContent className="cancellation-feature-content">
                <CheckCircle2 className="cancellation-icon" />
                <h3 className="cancellation-feature-title">Generate cancellation email templates</h3>
                <p className="cancellation-feature-desc">
                  Ready-to-send email templates for services requiring email cancellations.
                </p>
              </CardContent>
            </Card>
            
            <Card className="cancellation-feature-card">
              <CardContent className="cancellation-feature-content">
                <AlertCircle className="cancellation-icon" />
                <h3 className="cancellation-feature-title">Show what action is needed</h3>
                <p className="cancellation-feature-desc">
                  Clear guidance on exactly what steps to take before renewal.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="cancellation-cta-box">
            <p className="cancellation-cta-text">Avoid the charge before it happens.</p>
            <Button 
              onClick={() => document.getElementById('trials-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="cancellation-cta-btn"
            >
              Join Early Access
            </Button>
          </div>
        </div>
      </section>

      {/* Why Continuous Monitoring */}
      <section className="trials-continuous-section">
        <div className="section-container">
          <h2 className="section-title">Trial Charges Are Not a One-Time Problem</h2>
          <p className="section-description-large">
            Even careful people forget trials sometimes. New trials, subscriptions, renewals, 
            and price changes appear continuously over time.
          </p>
          
          <div className="continuous-highlight">
            <AlertCircle className="continuous-icon" />
            <p className="continuous-text">
              Financial leaks are not a one-time problem. Monitoring should not be one-time either.
            </p>
          </div>
          
          <p className="section-description">
            MySentinel continuously watches for new trial-to-paid risks and alerts you before they become costly.
          </p>
        </div>
      </section>

      {/* Trust and Privacy Section */}
      <section className="trials-trust-section">
        <div className="section-container">
          <h2 className="section-title">Built for Trust and Privacy</h2>
          <p className="section-description">
            Financial tools require a high level of trust. This platform is designed with a privacy-first 
            approach to ensure your financial data is secure and under your control.
          </p>
          
          <div className="trust-grid">
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Eye className="trust-icon" />
                </div>
                <h3 className="trust-card-title">Read-only Access</h3>
                <p className="trust-card-description">
                  We only analyze your financial data. We cannot move or modify your money.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Shield className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Money Movement</h3>
                <p className="trust-card-description">
                  The system does not initiate transactions or transfers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Lock className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Data Selling</h3>
                <p className="trust-card-description">
                  Your personal financial data is never sold or shared with third parties.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Zap className="trust-icon" />
                </div>
                <h3 className="trust-card-title">On-device Processing</h3>
                <p className="trust-card-description">
                  Sensitive insights are generated locally on your device to minimize data exposure (where possible).
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="trust-footer">
            <Shield className="trust-footer-icon" />
            <p className="trust-footer-text">
              We only analyze your financial activity to generate insights. We cannot move, withdraw, or transfer your money.
            </p>
          </div>
          
          <div className="trust-footer-secondary">
            <p>Your financial data belongs to you — always.</p>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="trials-demo-section">
        <div className="section-container">
          <h2 className="section-title">Explore the Interactive Demo</h2>
          <p className="section-description">
            See how MySentinel detects financial leaks, highlights upcoming trial charges, 
            and recommends timely actions.
          </p>
          
          <Button 
            onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
            className="demo-cta-btn"
            size="lg"
          >
            Explore Interactive Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="trials-waitlist" className="trials-waitlist-section">
        <div className="section-container">
          <div className="waitlist-header">
            <h2 className="section-title">Join the Waitlist</h2>
            <p className="waitlist-count">{waitlistCount.toLocaleString()} people waiting</p>
          </div>
          
          <div className="waitlist-form-container">
            {submitSuccess ? (
              <div className="success-message">
                <CheckCircle2 className="success-icon" />
                <h3>Thanks for joining!</h3>
                <p>You&apos;re on the list to help us stop surprise subscription charges.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <div className="form-group">
                  <label htmlFor="trials-email" className="form-label">Email *</label>
                  <Input
                    id="trials-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="trials-country" className="form-label">Country (optional)</label>
                  <Input
                    id="trials-country"
                    type="text"
                    placeholder="e.g., United States"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group priority-problem-group">
                  <label className="form-label form-label-required">
                    What would make you sign up today? *
                  </label>
                  <p className="form-sublabel">
                    Select the one problem you would most like solved automatically.
                  </p>
                  <div className="radio-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="trial_reminder"
                        checked={priorityProblem === 'trial_reminder'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Never forget a free trial again</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="bill_increase"
                        checked={priorityProblem === 'bill_increase'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Catch bill increases automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="credit_protection"
                        checked={priorityProblem === 'credit_protection'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Protect my credit score automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="reduce_interest"
                        checked={priorityProblem === 'reduce_interest'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Reduce unnecessary credit card interest</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="find_subscriptions"
                        checked={priorityProblem === 'find_subscriptions'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Find hidden subscriptions and recurring charges</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="continuous_monitoring"
                        checked={priorityProblem === 'continuous_monitoring'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Continuously monitor my finances for money leaks</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="other"
                        checked={priorityProblem === 'other'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Other</span>
                    </label>
                    
                    {priorityProblem === 'other' && (
                      <div className="other-problem-input">
                        <Input
                          type="text"
                          placeholder="Please specify"
                          value={otherProblem}
                          onChange={(e) => setOtherProblem(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="submit-btn"
                  size="lg"
                >
                  {isSubmitting ? 'Joining...' : 'Join Early Access'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="trials-final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">
            Stop Surprise Trial Charges Before They Happen
          </h2>
          <p className="final-cta-subtitle">
            Join early access and help shape a proactive financial watchdog that alerts you 
            before small money leaks become real losses.
          </p>
          <div className="final-cta-buttons">
            <Button 
              onClick={() => document.getElementById('trials-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="final-cta-btn"
              size="lg"
            >
              Join Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => handleDemoClick('https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1')}
              variant="outline"
              className="final-demo-btn"
              size="lg"
            >
              Try Interactive Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Feedback Popup */}
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default TrialsPage;
